import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Box, Card, CircularProgress, IconButton, Tooltip, Typography } from '@mui/joy';
import { Info, Refresh, VideoLibrary, Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { listFootageShares } from '../services/observabilityAdminApi';
import FootageSharesTable from './FootageSharesTable';

/** Footage shares of one hub, newest first (developer scope; 403 shows a notice). */
const FootageSharesSection = ({ hubId }) => {
    const { session } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const load = useCallback(async () => {
        if (!sessionRef.current?.accessToken) return;
        try {
            setLoading(true);
            setError(null);
            setForbidden(false);
            const { ok, status, body } = await listFootageShares({ hub_id: hubId, limit: 100 }, sessionRef.current);
            if (status === 403) {
                setForbidden(true);
                setItems([]);
                return;
            }
            if (!ok) {
                setError(body?.error || `Failed to load footage shares (${status}).`);
                return;
            }
            setItems(body?.items || []);
        } catch (e) {
            setError('Unable to reach the admin API.');
        } finally {
            setLoading(false);
        }
    }, [hubId]);

    useEffect(() => {
        load();
    }, [load, session?.accessToken]);

    return (
        <Box sx={{ mb: 4 }}>
            <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <VideoLibrary sx={{ mr: 1 }} />
                Footage Shares
                {!forbidden && (
                    <Badge badgeContent={items.length} color="primary" sx={{ ml: 2 }}>
                        <Box />
                    </Badge>
                )}
                {!forbidden && (
                    <Tooltip title="Reload footage shares">
                        <IconButton size="sm" variant="outlined" sx={{ ml: 'auto' }} onClick={load} disabled={loading}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                )}
            </Typography>

            {forbidden ? (
                <Alert color="warning" variant="soft" startDecorator={<Warning />}>
                    <Box>
                        <Typography level="title-sm">Developer access required</Typography>
                        <Typography level="body-sm" textColor="neutral.600">
                            You need Developer Access (read or write) to view footage shares.
                        </Typography>
                    </Box>
                </Alert>
            ) : loading && items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size="sm" />
                </Card>
            ) : error ? (
                <Alert color="danger" variant="soft">{error}</Alert>
            ) : items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="body-sm" textColor="neutral.500">
                        No footage shared from this hub yet
                    </Typography>
                </Card>
            ) : (
                <FootageSharesTable items={items} showHub={false} />
            )}
        </Box>
    );
};

export default FootageSharesSection;
